import { useState } from 'react';
import '../styles/responsive/pages/Guide.css';
import {
  BookOpen,
  FileText,
  Code2,
  Key,
  Webhook,
  Shield,
  CreditCard,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  Zap,
  Link2,
  AlertCircle,
  CheckCircle,
  Terminal,
} from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';

const Guide = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [copiedCode, setCopiedCode] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const isMobile = useIsMobile();

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Zap },
    { id: 'dashboard-overview', label: 'Dashboard Overview', icon: BookOpen },
    { id: 'invoices', label: 'Creating Invoices', icon: FileText },
    { id: 'payment-links', label: 'Payment Links', icon: Link2 },
    { id: 'api-integration', label: 'API Integration', icon: Code2 },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'payment-sessions', label: 'Payment Sessions API', icon: CreditCard },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'security', label: 'Security Best Practices', icon: Shield },
  ];

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, language = 'javascript', id }) => (
    <div className="rd-code guide-code">
      <span className="guide-code-lang">{language}</span>
      <button
        className="rd-copy"
        onClick={() => copyToClipboard(code, id)}
      >
        {copiedCode === id ? <Check size={11} /> : <Copy size={11} />}
        {copiedCode === id ? 'Copied!' : 'Copy'}
      </button>
      <pre><code>{code}</code></pre>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="guide-content">
            <h1>Getting Started with MaxelPay</h1>
            <p className="intro">
              Welcome to MaxelPay! This guide will help you understand how to accept cryptocurrency payments
              for your business. Whether you're creating invoices manually or integrating via API, we've got you covered.
            </p>

            <div className="info-box success">
              <CheckCircle size={20} />
              <div>
                <strong>Quick Start</strong>
                <p>You can start accepting payments in just 3 steps: Create an API key, integrate our API, and start receiving crypto payments!</p>
              </div>
            </div>

            <h2>Prerequisites</h2>
            <ul>
              <li>A verified MaxelPay merchant account</li>
              <li>At least one wallet configured in Settings</li>
              <li>API keys generated (for API integration)</li>
            </ul>

            <h2>How MaxelPay Works</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Create Payment Request</h3>
                <p>Generate an invoice or payment link for your customer, specifying the amount in USD.</p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Customer Pays</h3>
                <p>Customer selects their preferred cryptocurrency and network, then sends payment to the provided address.</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Payment Confirmed</h3>
                <p>We monitor the blockchain, verify the payment, and notify you via webhook when complete.</p>
              </div>
            </div>

            <h2>Two Ways to Accept Payments</h2>
            <div className="options-grid">
              <div className="option-card">
                <FileText size={32} />
                <h3>Manual Invoices</h3>
                <p>Create invoices directly from the dashboard. Perfect for freelancers, small businesses, and one-off payments.</p>
                <button onClick={() => setActiveSection('invoices')}>
                  Learn More <ChevronRight size={16} />
                </button>
              </div>
              <div className="option-card">
                <Code2 size={32} />
                <h3>API Integration</h3>
                <p>Integrate our API into your application for automated payment processing. Ideal for e-commerce and SaaS.</p>
                <button onClick={() => setActiveSection('api-integration')}>
                  Learn More <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        );

      case 'dashboard-overview':
        return (
          <div className="guide-content">
            <h1>Dashboard Overview</h1>
            <p className="intro">
              Your MaxelPay dashboard provides a complete view of your payment operations. Here's what each section offers.
            </p>

            <h2>Navigation</h2>
            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon home">Home</div>
                <div className="feature-desc">
                  <h4>Home</h4>
                  <p>Overview of recent activity, quick stats, and getting started checklist.</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon balance">Balance</div>
                <div className="feature-desc">
                  <h4>Balance</h4>
                  <p>View your total revenue, invoice revenue, session revenue, and overpaid payments.</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon links">Links</div>
                <div className="feature-desc">
                  <h4>Payment Links</h4>
                  <p>Create and manage reusable payment links and view payment sessions.</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon transactions">Txns</div>
                <div className="feature-desc">
                  <h4>Transactions</h4>
                  <p>Monitor all blockchain transactions, their status, and confirmation details.</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon invoices">Invoices</div>
                <div className="feature-desc">
                  <h4>Invoices</h4>
                  <p>Create, send, and track invoices. View payment status and history.</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon developer">Dev</div>
                <div className="feature-desc">
                  <h4>Developer</h4>
                  <p>Manage API keys, view integration settings, and access API documentation.</p>
                </div>
              </div>
            </div>

            <h2>Test Mode vs Live Mode</h2>
            <p>Use the toggle in the header to switch between Test and Live modes:</p>
            <div className="info-box warning">
              <AlertCircle size={20} />
              <div>
                <strong>Test Mode</strong>
                <p>Use testnet cryptocurrencies to test your integration without real funds. API keys starting with <code>pk_test_</code> are for test mode.</p>
              </div>
            </div>
            <div className="info-box success">
              <CheckCircle size={20} />
              <div>
                <strong>Live Mode</strong>
                <p>Process real cryptocurrency payments. API keys starting with <code>pk_live_</code> are for live mode.</p>
              </div>
            </div>
          </div>
        );

      case 'invoices':
        return (
          <div className="guide-content">
            <h1>Creating Invoices</h1>
            <p className="intro">
              Invoices are perfect for billing customers for products or services. Create professional invoices
              that your customers can pay with cryptocurrency.
            </p>

            <h2>Creating an Invoice</h2>
            <ol className="numbered-list">
              <li>
                <strong>Navigate to Invoices</strong>
                <p>Click on "Invoices" in the sidebar navigation.</p>
              </li>
              <li>
                <strong>Click "Create Invoice"</strong>
                <p>Click the button in the top right corner to open the invoice form.</p>
              </li>
              <li>
                <strong>Fill in Customer Details</strong>
                <p>Enter the customer's name and email address. They will receive payment notifications.</p>
              </li>
              <li>
                <strong>Add Line Items</strong>
                <p>Add products or services with descriptions, quantities, and prices.</p>
              </li>
              <li>
                <strong>Set Due Date (Optional)</strong>
                <p>Specify when the payment is due.</p>
              </li>
              <li>
                <strong>Send Invoice</strong>
                <p>Click "Create & Send" to email the invoice to your customer with a payment link.</p>
              </li>
            </ol>

            <h2>Invoice Statuses</h2>
            <div className="status-grid">
              <div className="status-item draft">
                <span className="status-badge">Draft</span>
                <p>Invoice created but not yet sent to customer.</p>
              </div>
              <div className="status-item pending">
                <span className="status-badge">Pending</span>
                <p>Invoice sent, awaiting payment from customer.</p>
              </div>
              <div className="status-item processing">
                <span className="status-badge">Processing</span>
                <p>Payment detected, waiting for blockchain confirmation.</p>
              </div>
              <div className="status-item paid">
                <span className="status-badge">Paid</span>
                <p>Full payment received and confirmed.</p>
              </div>
              <div className="status-item partial">
                <span className="status-badge">Partially Paid</span>
                <p>Customer has paid less than the invoice amount.</p>
              </div>
              <div className="status-item overpaid">
                <span className="status-badge">Overpaid</span>
                <p>Customer paid more than the invoice amount.</p>
              </div>
            </div>

            <h2>Sharing Invoices</h2>
            <p>Each invoice has a unique payment link that you can share:</p>
            <CodeBlock
              id="invoice-link"
              language="text"
              code={`https://your-domain.com/pay/invoice/inv_abc123xyz`}
            />
            <p>You can also share the read-only invoice view:</p>
            <CodeBlock
              id="invoice-view"
              language="text"
              code={`https://your-domain.com/invoice/inv_abc123xyz`}
            />
          </div>
        );

      case 'payment-links':
        return (
          <div className="guide-content">
            <h1>Payment Links</h1>
            <p className="intro">
              Payment Links are reusable links that can accept multiple payments. Perfect for donations,
              tips, or products with a fixed price.
            </p>

            <h2>Creating a Payment Link</h2>
            <ol className="numbered-list">
              <li>
                <strong>Navigate to Payment Links</strong>
                <p>Click on "Payment Links" in the sidebar.</p>
              </li>
              <li>
                <strong>Click "Create Payment Link"</strong>
                <p>Open the creation form.</p>
              </li>
              <li>
                <strong>Configure the Link</strong>
                <p>Set the name, amount (optional for variable amounts), and description.</p>
              </li>
              <li>
                <strong>Copy & Share</strong>
                <p>Copy the generated link and share it with your customers.</p>
              </li>
            </ol>

            <h2>Payment Link vs Invoice</h2>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Payment Link</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Reusable</td>
                  <td><CheckCircle size={16} className="check" /></td>
                  <td>One-time</td>
                </tr>
                <tr>
                  <td>Customer Details</td>
                  <td>Optional</td>
                  <td>Required</td>
                </tr>
                <tr>
                  <td>Line Items</td>
                  <td>No</td>
                  <td><CheckCircle size={16} className="check" /></td>
                </tr>
                <tr>
                  <td>Due Date</td>
                  <td>No</td>
                  <td><CheckCircle size={16} className="check" /></td>
                </tr>
                <tr>
                  <td>Email Notifications</td>
                  <td>Optional</td>
                  <td><CheckCircle size={16} className="check" /></td>
                </tr>
                <tr>
                  <td>Best For</td>
                  <td>Donations, Tips, Products</td>
                  <td>Services, Custom Orders</td>
                </tr>
              </tbody>
            </table>
          </div>
        );

      case 'api-integration':
        return (
          <div className="guide-content">
            <h1>API Integration</h1>
            <p className="intro">
              Integrate MaxelPay into your application to automate payment processing. Our REST API
              allows you to create payment sessions programmatically.
            </p>

            <h2>Base URL</h2>
            <CodeBlock
              id="base-url"
              language="text"
              code={`Production: https://api.maxelpay.com/api/v1
Test Mode:  https://api.maxelpay.com/api/v1 (use test API keys)`}
            />

            <h2>Authentication</h2>
            <p>All API requests are authenticated with your API key and API secret in the headers:</p>
            <CodeBlock
              id="auth-header"
              language="http"
              code={`X-API-KEY: pk_live_your_api_key_here
X-API-SECRET: sk_your_api_secret_here`}
            />
            <p>
              Both are under Developer → API Keys. The secret is a password: keep it on your server only,
              never in a browser or mobile app.
            </p>

            <h2>Quick Start Example</h2>
            <p>Here's a complete example to create a payment session:</p>
            <CodeBlock
              id="quick-start"
              language="javascript"
              code={`// Node.js Example
const axios = require('axios');

const createPayment = async () => {
  const response = await axios.post(
    'https://api.maxelpay.com/api/v1/payments/sessions',
    {
      orderId: 'ORDER-12345',
      amount: 99.99,
      currency: 'USD',
      description: 'Premium Subscription',
      customerEmail: 'customer@example.com',
      customerName: 'John Doe',
      successUrl: 'https://yoursite.com/success',
      cancelUrl: 'https://yoursite.com/cancel',
      callbackUrl: 'https://yoursite.com/api/webhook'
    },
    {
      headers: {
        'X-API-KEY': 'pk_live_your_api_key',
        'X-API-SECRET': process.env.MAXELPAY_API_SECRET,
        'Content-Type': 'application/json'
      }
    }
  );

  console.log('Payment URL:', response.data.data.paymentUrl);
  return response.data;
};

createPayment();`}
            />

            <h2>Response Example</h2>
            <CodeBlock
              id="response-example"
              language="json"
              code={`{
  "success": true,
  "message": "Payment session created successfully",
  "data": {
    "sessionId": "sess_abc123xyz",
    "paymentUrl": "https://pay.maxelpay.com/pay/session/sess_abc123xyz",
    "orderId": "ORDER-12345",
    "amount": 99.99,
    "currency": "USD",
    "status": "pending",
    "expiresAt": "2024-01-15T12:30:00Z",
    "isTestnet": false
  }
}`}
            />

            <div className="info-box">
              <Terminal size={20} />
              <div>
                <strong>Redirect Customer</strong>
                <p>After creating a session, redirect your customer to the <code>paymentUrl</code> to complete payment.</p>
              </div>
            </div>
          </div>
        );

      case 'api-keys':
        return (
          <div className="guide-content">
            <h1>API Keys</h1>
            <p className="intro">
              API keys authenticate your requests to the MaxelPay API. Keep them secure and never expose them in client-side code.
            </p>

            <h2>Generating API Keys</h2>
            <ol className="numbered-list">
              <li>
                <strong>Go to Developer Settings</strong>
                <p>Navigate to "Developer" in the sidebar.</p>
              </li>
              <li>
                <strong>Click "Create API Key"</strong>
                <p>Give your key a descriptive name (e.g., "Production Server", "Staging").</p>
              </li>
              <li>
                <strong>Configure Settings</strong>
                <p>Set the default currency and webhook URL for this key.</p>
              </li>
              {/* <li>
                <strong>Save Your Secret Key</strong>
                <p>Copy your secret key immediately - it won't be shown again!</p>
              </li> */}
            </ol>

            <h2>Key Types</h2>
            <div className="key-types">
              <div className="key-type">
                <h4>Public Key (API Key)</h4>
                <code>pk_live_xxxxxxxxxxxxxxxx</code>
                <p>Used in the <code>X-API-KEY</code> header for API requests.</p>
              </div>
              <div className="key-type">
                <h4>API Secret</h4>
                <code>sk_xxxxxxxxxxxxxxxx</code>
                <p>
                  Sent in the <code>X-API-SECRET</code> header with your API key, and used to verify payment
                  session webhook signatures. Server-side only. Reveal or rotate it under API Keys.
                </p>
              </div>
            </div>

            <h2>Test vs Live Keys</h2>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Prefix</th>
                  <th>Use Case</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Test Public Key</td>
                  <td><code>pk_test_</code></td>
                  <td>Testing with testnet crypto</td>
                </tr>
                {/* <tr>
                  <td>Test Secret Key</td>
                  <td><code>sk_test_</code></td>
                  <td>Verifying test webhooks</td>
                </tr> */}
                <tr>
                  <td>Live Public Key</td>
                  <td><code>pk_live_</code></td>
                  <td>Production payments</td>
                </tr>
                {/* <tr>
                  <td>Live Secret Key</td>
                  <td><code>sk_live_</code></td>
                  <td>Verifying live webhooks</td>
                </tr> */}
              </tbody>
            </table>

            <div className="info-box warning">
              <AlertCircle size={20} />
              <div>
                <strong>Security Warning</strong>
                <p>Never expose your API keys in client-side JavaScript, public repositories, or logs. Use environment variables to store them securely.</p>
              </div>
            </div>
          </div>
        );

      case 'payment-sessions':
        return (
          <div className="guide-content">
            <h1>Payment Sessions API</h1>
            <p className="intro">
              Payment Sessions are the core of API integration. Each session represents a single payment request.
            </p>

            <h2>Create Payment Session</h2>
            <div className="endpoint">
              <span className="method post">POST</span>
              <code>/v1/payments/sessions</code>
            </div>

            <h3>Request Parameters</h3>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>orderId</code></td>
                  <td>string</td>
                  <td>Yes</td>
                  <td>Your unique order identifier</td>
                </tr>
                <tr>
                  <td><code>amount</code></td>
                  <td>number</td>
                  <td>Yes</td>
                  <td>Payment amount (minimum 0.01)</td>
                </tr>
                <tr>
                  <td><code>currency</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Currency code (default: USD)</td>
                </tr>
                <tr>
                  <td><code>description</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Payment description shown to customer</td>
                </tr>
                <tr>
                  <td><code>customerEmail</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Customer's email for notifications</td>
                </tr>
                <tr>
                  <td><code>customerName</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Customer's name</td>
                </tr>
                <tr>
                  <td><code>successUrl</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Redirect URL after successful payment</td>
                </tr>
                <tr>
                  <td><code>cancelUrl</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Redirect URL if customer cancels</td>
                </tr>
                <tr>
                  <td><code>callbackUrl</code></td>
                  <td>string</td>
                  <td>No</td>
                  <td>Webhook URL for payment notifications</td>
                </tr>
                <tr>
                  <td><code>expirationMinutes</code></td>
                  <td>integer</td>
                  <td>No</td>
                  <td>Session expiry time (5-1440 mins, default: 30)</td>
                </tr>
                <tr>
                  <td><code>metadata</code></td>
                  <td>object</td>
                  <td>No</td>
                  <td>Custom key-value data for your use</td>
                </tr>
              </tbody>
            </table>

            <h3>Example Request</h3>
            <CodeBlock
              id="create-session"
              language="bash"
              code={`curl -X POST https://api.maxelpay.com/api/v1/payments/sessions \\
  -H "X-API-KEY: pk_live_your_api_key" \\
  -H "X-API-SECRET: sk_your_api_secret" \\
  -H "Content-Type: application/json" \\
  -d '{
    "orderId": "ORDER-12345",
    "amount": 150.00,
    "currency": "USD",
    "description": "Annual Subscription",
    "customerEmail": "john@example.com",
    "successUrl": "https://yoursite.com/success",
    "callbackUrl": "https://yoursite.com/webhooks/maxelpay",
    "metadata": {
      "userId": "user_123",
      "plan": "premium"
    }
  }'`}
            />

            <h2>Get Session Status</h2>
            <div className="endpoint">
              <span className="method get">GET</span>
              <code>/v1/payments/sessions/:sessionId/status</code>
            </div>

            <h3>Session Statuses</h3>
            <div className="status-grid compact">
              <div className="status-item pending">
                <code>pending</code>
                <p>Awaiting payment</p>
              </div>
              <div className="status-item processing">
                <code>processing</code>
                <p>Wallet assigned, awaiting transaction</p>
              </div>
              <div className="status-item paid">
                <code>paid</code>
                <p>Payment completed</p>
              </div>
              <div className="status-item partial">
                <code>partially_paid</code>
                <p>Partial payment received</p>
              </div>
              <div className="status-item overpaid">
                <code>overpaid</code>
                <p>Excess payment received</p>
              </div>
              <div className="status-item expired">
                <code>expired</code>
                <p>Session timed out</p>
              </div>
              <div className="status-item cancelled">
                <code>cancelled</code>
                <p>Session cancelled</p>
              </div>
            </div>
          </div>
        );

      case 'webhooks':
        return (
          <div className="guide-content">
            <h1>Webhooks</h1>
            <p className="intro">
              Webhooks notify your server when payment events occur. This is the recommended way to
              handle payment confirmations.
            </p>

            <h2>Setting Up Webhooks</h2>
            <ol className="numbered-list">
              <li>
                <strong>Create an Endpoint</strong>
                <p>Set up an HTTPS endpoint on your server to receive webhook events.</p>
              </li>
              <li>
                <strong>Configure Webhook URL</strong>
                <p>Add your webhook URL when creating an API key or include it in each payment session.</p>
              </li>
              <li>
                <strong>Verify Signatures</strong>
                <p>Always verify webhook signatures to ensure requests come from MaxelPay.</p>
              </li>
            </ol>

            <h2>Webhook Payload</h2>
            <CodeBlock
              id="webhook-payload"
              language="json"
              code={`{
  "event": "payment.completed",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "sessionId": "sess_abc123xyz",
    "orderId": "ORDER-12345",
    "status": "paid",
    "amount": 99.99,
    "currency": "USD",
    "paidAmount": 99.99,
    "totalPaidUsd": 99.99,
    "txHash": "0x123...abc",
    "network": "Ethereum Sepolia",
    "tokenSymbol": "USDT",
    "customerEmail": "customer@example.com",
    "metadata": {
      "userId": "user_123"
    }
  }
}`}
            />

            <h2>Verifying Webhook Signatures</h2>
            <p>
              Each payment session webhook carries an HMAC-SHA256 signature (hex) of the raw request body in the{' '}
              <code>X-Webhook-Signature</code> header, made with your API secret (<code>sk_...</code>). Verify it
              against the raw bytes you received, before parsing: re-serializing the parsed JSON can change the
              bytes in other languages.
            </p>
            <CodeBlock
              id="verify-signature"
              language="javascript"
              code={`const crypto = require('crypto');
const express = require('express');

const app = express();

// Keep the raw body: the signature is computed over the exact bytes sent.
app.use('/webhooks/maxelpay', express.json({
  verify: (req, _res, buf) => { req.rawBody = buf; },
}));

function isValidSignature(rawBody, signature, secret) {
  if (!rawBody || !signature) return false;
  const expected = crypto.createHmac('sha256', secret).update(rawBody).digest('hex');
  // timingSafeEqual throws on unequal lengths, so compare lengths first.
  return signature.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));
}

app.post('/webhooks/maxelpay', async (req, res) => {
  const signature = req.get('x-webhook-signature');
  if (!isValidSignature(req.rawBody, signature, process.env.MAXELPAY_API_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const { event, data } = req.body;

  // The same payment can arrive more than once (manual resend, a second URL),
  // so make fulfilment idempotent: do it once per orderId.
  switch (event) {
    case 'payment.completed':
    case 'payment.overpaid':
      await fulfillOrderOnce(data.orderId, data.txHash);
      break;
    case 'payment.partial':
      await recordPartialPayment(data.orderId, data.totalPaidUsd, data.remainingAmountUsd);
      break;
    case 'payment.expired':
    case 'payment.failed':
      await releaseOrder(data.orderId);
      break;
    default:
      break; // ignore events you do not use
  }

  // Answer 2xx quickly; do slow work after responding.
  res.json({ received: true });
});

app.listen(3000);`}
            />

            <h2>Webhook Events</h2>
            <p>Sent to the session's <code>callbackUrl</code> (or the API key's webhook URL):</p>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>payment.completed</code></td>
                  <td>Payment has been fully confirmed on the blockchain. Fulfill the order.</td>
                </tr>
                <tr>
                  <td><code>payment.overpaid</code></td>
                  <td>Customer paid more than the required amount. Fulfill the order.</td>
                </tr>
                <tr>
                  <td><code>payment.partial</code></td>
                  <td>Partial payment received (less than the required amount). Do not fulfill yet.</td>
                </tr>
                <tr>
                  <td><code>payment.expired</code></td>
                  <td>Payment session expired without full payment</td>
                </tr>
                <tr>
                  <td><code>payment.failed</code></td>
                  <td>Payment session failed</td>
                </tr>
              </tbody>
            </table>
            <p>
              If you also set a <code>successUrl</code>, it receives its own notification with these events:{' '}
              <code>payment.confirmed</code>, <code>payment.overpaid_confirmed</code>,{' '}
              <code>payment.partial_confirmed</code>, <code>payment.unsupported_token</code>. Use different URLs
              for <code>callbackUrl</code> and <code>successUrl</code>, or expect two notifications per payment.
            </p>

            <div className="info-box warning">
              <AlertCircle size={20} />
              <div>
                <strong>Respond Quickly</strong>
                <p>
                  Respond with a 2xx status within 30 seconds; redirects count as a failed delivery. Each webhook
                  is sent once and is not retried automatically. Failed deliveries are listed under Developer →
                  Webhooks, where you can resend them.
                </p>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="guide-content">
            <h1>Security Best Practices</h1>
            <p className="intro">
              Follow these guidelines to keep your integration secure and protect your customers.
            </p>

            <h2>API Key Security</h2>
            <div className="security-checklist">
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Store API keys in environment variables, never in code</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Use different API keys for development and production</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Rotate API keys periodically and after any suspected breach</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Never expose API keys in client-side JavaScript</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Add your API keys to .gitignore to prevent accidental commits</span>
              </div>
            </div>

            <h2>Webhook Security</h2>
            <div className="security-checklist">
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Always verify webhook signatures before processing</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Use HTTPS endpoints only for webhooks</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Implement idempotency - handle duplicate webhooks gracefully</span>
              </div>
              <div className="check-item">
                <CheckCircle size={18} />
                <span>Don't rely solely on webhooks - verify payment status via API</span>
              </div>
            </div>

            <h2>Payment Verification</h2>
            <CodeBlock
              id="verify-payment"
              language="javascript"
              code={`// Always verify payment status server-side before fulfilling orders
async function verifyAndFulfillOrder(sessionId) {
  const response = await axios.get(
    \`https://api.maxelpay.com/api/v1/payments/sessions/\${sessionId}/status\`,
    { headers: { 'X-API-KEY': process.env.MAXELPAY_API_KEY } }
  );

  const { status, amount, orderId } = response.data.data;

  if (status === 'paid' || status === 'overpaid') {
    // Verify amount matches your expected amount
    const order = await getOrder(orderId);
    if (amount >= order.total) {
      await fulfillOrder(orderId);
    }
  }
}`}
            />

            <h2>Environment Configuration</h2>
            <CodeBlock
              id="env-example"
              language="bash"
              code={`# .env file (never commit this!)
MAXELPAY_API_KEY=pk_live_your_api_key
MAXELPAY_API_SECRET=sk_your_api_secret

# For testing (each key has its own secret)
MAXELPAY_TEST_API_KEY=pk_test_your_test_key
MAXELPAY_TEST_API_SECRET=sk_your_test_api_secret`}
            />

            <div className="info-box">
              <Shield size={20} />
              <div>
                <strong>Need Help?</strong>
                <p>If you suspect any security issues with your account, contact our support team immediately.</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
    <div className="rd-page-header guide-page-header">
      <div>
        <h1 className="rd-page-title">Guide</h1>
        <p className="rd-page-subtitle">
          Integration docs and getting-started walkthroughs for accepting crypto payments.
        </p>
      </div>
    </div>
    <div className="guide-page">
      {/* Sidebar Navigation */}
      <aside className="guide-sidebar rd-card">
        <div className="guide-sidebar-header">
          <span className="rd-icon-tile guide-doc-tile"><BookOpen size={18} /></span>
          <span>Documentation</span>
        </div>
        {isMobile && (
          <button
            className="guide-nav-toggle"
            onClick={() => setNavOpen((o) => !o)}
            aria-expanded={navOpen}
            aria-controls="guide-nav"
          >
            <span className="guide-nav-toggle-active">
              {sections.find((s) => s.id === activeSection)?.label ?? 'Select section'}
            </span>
            <span className="guide-nav-toggle-label">
              <ChevronRight size={14} className={`guide-nav-toggle-chevron${navOpen ? ' open' : ''}`} />
            </span>
          </button>
        )}
        <nav id="guide-nav" className={`guide-nav${isMobile && navOpen ? ' nav-open' : ''}`}>
          {sections.map((section) => (
            <button
              key={section.id}
              className={`guide-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(section.id);
                if (isMobile) setNavOpen(false);
              }}
            >
              <section.icon size={16} />
              <span>{section.label}</span>
              {activeSection === section.id && <ChevronRight size={14} className="active-indicator" />}
            </button>
          ))}
        </nav>

      </aside>

      {/* Main Content */}
      <main className="guide-main rd-card">
        {renderContent()}
      </main>

      <style>{`
        .guide-page {
          display: flex;
          align-items: flex-start;
          gap: 20px;
        }

        /* Sidebar */
        .guide-sidebar {
          width: 250px;
          flex-shrink: 0;
          padding: 12px;
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 16px;
        }

        .guide-sidebar-header {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.25rem 0.5rem 0.875rem;
          margin-bottom: 0.5rem;
          border-bottom: 1px solid var(--line);
          font-weight: 700;
          font-size: 0.875rem;
          color: var(--ink);
        }

        .guide-doc-tile { width: 32px; height: 32px; border-radius: var(--r-sm); }

        .guide-nav {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .guide-nav-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          width: 100%;
          padding: 0.5rem 0.625rem;
          border: none;
          background: transparent;
          border-radius: var(--r-sm);
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--ink-secondary);
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          text-align: left;
          min-height: 38px;
          font-family: var(--iv-sans);
        }

        .guide-nav-item:hover {
          background: var(--bg-inset);
          color: var(--ink);
        }

        .guide-nav-item.active {
          background: var(--accent-soft);
          color: var(--accent-ink);
          font-weight: 600;
        }

        .guide-nav-item .active-indicator {
          margin-left: auto;
        }

        /* Main Content */
        .guide-main {
          flex: 1;
          min-width: 0;
          padding: 28px 32px;
        }

        .guide-content h1 {
          font-size: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--ink);
          margin: 0 0 0.5rem 0;
        }

        .guide-content .intro {
          font-size: 0.9375rem;
          color: var(--ink-secondary);
          margin-bottom: 1.75rem;
          line-height: 1.6;
        }

        .guide-content h2 {
          font-size: 1.0625rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
          margin: 2.25rem 0 1rem 0;
          padding-top: 1.25rem;
          border-top: 1px solid var(--line);
        }

        .guide-content h2:first-of-type {
          border-top: none;
          padding-top: 0;
        }

        .guide-content h3 {
          font-size: 0.9375rem;
          font-weight: 700;
          color: var(--ink);
          margin: 1.5rem 0 0.75rem 0;
        }

        .guide-content h4 {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--ink);
          margin: 0 0 0.25rem 0;
        }

        .guide-content p {
          color: var(--ink-secondary);
          font-size: 0.875rem;
          line-height: 1.7;
          margin-bottom: 1rem;
        }

        .guide-content ul, .guide-content ol {
          color: var(--ink-secondary);
          font-size: 0.875rem;
          padding-left: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .guide-content li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }

        .guide-content code {
          background: var(--bg-inset);
          border: 1px solid var(--line);
          padding: 0.0625rem 0.375rem;
          border-radius: 5px;
          font-family: var(--font-mono);
          font-size: 0.8125em;
          color: var(--accent-ink);
        }

        /* Code Block (rd-code + rd-copy) */
        .guide-code {
          margin: 1rem 0 1.5rem;
          padding-top: 36px;
        }

        .guide-code-lang {
          position: absolute;
          top: 12px;
          left: 16px;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #71717a;
        }

        .guide-code .rd-copy {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .guide-code pre {
          margin: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .guide-code code {
          background: none;
          border: none;
          padding: 0;
          color: #e4e4e7;
          font-family: var(--font-mono);
          font-size: 0.78125rem;
          line-height: 1.6;
          white-space: pre;
        }

        /* Info Box */
        .info-box {
          display: flex;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border-radius: var(--r-md);
          margin: 1rem 0;
          background: var(--accent-soft);
          border: 1px solid var(--line);
        }

        .info-box svg {
          flex-shrink: 0;
          color: var(--accent);
          margin-top: 2px;
        }

        .info-box strong {
          display: block;
          color: var(--ink);
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .info-box p {
          margin: 0;
          font-size: 0.8125rem;
        }

        .info-box.warning {
          background: var(--warn-soft);
        }

        .info-box.warning svg {
          color: var(--warn);
        }

        .info-box.success {
          background: var(--ok-soft);
        }

        .info-box.success svg {
          color: var(--ok);
        }

        /* Steps Grid */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .step-card {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          padding: 1.25rem;
          box-shadow: var(--sh-sm);
        }

        .step-number {
          width: 32px;
          height: 32px;
          background: var(--accent-soft);
          border-radius: var(--r-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent);
          font-weight: 700;
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
        }

        .step-card h3 {
          font-size: 0.9375rem;
          margin: 0 0 0.375rem 0;
          color: var(--ink);
        }

        .step-card p {
          font-size: 0.8125rem;
          margin: 0;
          color: var(--ink-secondary);
        }

        /* Options Grid */
        .options-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .option-card {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          padding: 1.5rem;
          box-shadow: var(--sh-sm);
          transition: box-shadow 0.15s, border-color 0.15s;
        }

        .option-card:hover {
          border-color: var(--line-strong);
          box-shadow: var(--sh-md);
        }

        .option-card > svg {
          color: var(--accent);
          margin-bottom: 1rem;
        }

        .option-card h3 {
          font-size: 1rem;
          margin: 0 0 0.5rem 0;
          color: var(--ink);
        }

        .option-card p {
          font-size: 0.875rem;
          margin: 0 0 1rem 0;
          color: var(--ink-secondary);
        }

        .option-card button {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0;
          background: none;
          border: none;
          color: var(--accent);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          font-family: var(--iv-sans);
        }

        .option-card button:hover {
          text-decoration: underline;
        }

        /* Feature List */
        .feature-list {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.875rem 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          box-shadow: var(--sh-sm);
        }

        .feature-icon {
          width: 44px;
          height: 44px;
          border-radius: var(--r-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--accent-ink);
          background: var(--accent-soft);
          flex-shrink: 0;
        }

        .feature-icon.balance { background: var(--ok-soft); color: var(--ok); }
        .feature-icon.transactions { background: var(--warn-soft); color: var(--warn); }
        .feature-icon.invoices { background: var(--info-soft); color: var(--info); }

        .feature-desc h4 { margin-bottom: 0.125rem; }
        .feature-desc p {
          margin: 0;
          font-size: 0.8125rem;
        }

        /* Numbered List */
        .numbered-list {
          counter-reset: step;
          list-style: none;
          padding-left: 0;
        }

        .numbered-list li {
          position: relative;
          padding-left: 2.75rem;
          margin-bottom: 1.25rem;
        }

        .numbered-list li::before {
          counter-increment: step;
          content: counter(step);
          position: absolute;
          left: 0;
          top: 0;
          width: 28px;
          height: 28px;
          background: var(--accent-soft);
          border-radius: var(--r-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 700;
          color: var(--accent);
        }

        .numbered-list li strong {
          display: block;
          color: var(--ink);
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .numbered-list li p {
          margin: 0;
          font-size: 0.8125rem;
        }

        /* Status Grid */
        .status-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.875rem;
          margin: 1.5rem 0;
        }

        .status-grid.compact {
          grid-template-columns: repeat(4, 1fr);
        }

        .status-item {
          padding: 0.875rem 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          box-shadow: var(--sh-sm);
        }

        .status-badge {
          display: inline-block;
          padding: 0.1875rem 0.5rem;
          border-radius: var(--r-pill);
          font-size: 0.6875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .status-item code {
          display: inline-block;
          padding: 0.1875rem 0.5rem;
          border-radius: var(--r-sm);
          font-family: var(--font-mono);
          font-size: 0.6875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          border: none;
        }

        .status-item p {
          margin: 0;
          font-size: 0.8125rem;
          color: var(--ink-secondary);
        }

        .status-item.draft .status-badge { background: var(--bg-inset); color: var(--ink-secondary); }
        .status-item.pending .status-badge, .status-item.pending code { background: var(--warn-soft); color: var(--warn); }
        .status-item.processing .status-badge, .status-item.processing code { background: var(--info-soft); color: var(--info); }
        .status-item.paid .status-badge, .status-item.paid code { background: var(--ok-soft); color: var(--ok); }
        .status-item.partial .status-badge, .status-item.partial code { background: var(--warn-soft); color: var(--warn); }
        .status-item.overpaid .status-badge, .status-item.overpaid code { background: var(--accent-soft); color: var(--accent-ink); }
        .status-item.expired code { background: var(--bg-inset); color: var(--ink-secondary); }
        .status-item.cancelled code { background: var(--err-soft); color: var(--err); }

        /* Comparison Table */
        .comparison-table, .params-table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.8125rem;
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          overflow: scroll;
        }

        .comparison-table th, .params-table th {
          text-align: left;
          padding: 0.625rem 0.875rem;
          background: var(--bg-subtle);
          border-bottom: 1px solid var(--line);
          font-weight: 700;
          color: var(--ink);
        }

        .comparison-table td, .params-table td {
          padding: 0.625rem 0.875rem;
          border-bottom: 1px solid var(--line);
          color: var(--ink-secondary);
        }

        .comparison-table tr:last-child td,
        .params-table tr:last-child td {
          border-bottom: none;
        }

        .comparison-table .check {
          color: var(--ok);
        }

        /* Endpoint */
        .endpoint {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: var(--bg-subtle);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          margin: 1rem 0;
        }

        .method {
          padding: 0.1875rem 0.5rem;
          border-radius: var(--r-sm);
          font-family: var(--font-mono);
          font-size: 0.6875rem;
          font-weight: 700;
        }

        .method.get { background: var(--ok-soft); color: var(--ok); }
        .method.post { background: var(--info-soft); color: var(--info); }

        .endpoint code {
          background: none;
          border: none;
          color: var(--ink);
          font-family: var(--font-mono);
          font-size: 0.8125rem;
          padding: 0;
        }

        /* Key Types */
        .key-types {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin: 1rem 0;
        }

        .key-type {
          padding: 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          box-shadow: var(--sh-sm);
        }

        .key-type h4 {
          margin-bottom: 0.5rem;
        }

        .key-type code {
          display: block;
          margin-bottom: 0.5rem;
        }

        .key-type p {
          margin: 0;
          font-size: 0.8125rem;
        }

        /* Security Checklist */
        .security-checklist {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin: 1rem 0;
        }

        .check-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          box-shadow: var(--sh-sm);
        }

        .check-item svg {
          color: var(--ok);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .check-item span {
          color: var(--ink-secondary);
          font-size: 0.875rem;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .guide-main {
            padding: 24px;
          }

          .steps-grid, .options-grid {
            grid-template-columns: 1fr;
          }

          .status-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .status-grid.compact {
            grid-template-columns: repeat(2, 1fr);
          }

          .key-types {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .guide-page {
            flex-direction: column;
            gap: 16px;
          }

          .guide-sidebar {
            width: 100%;
            position: static;
          }

          .guide-nav {
            flex-direction: row;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .guide-nav-item {
            flex: 0 0 auto;
            width: auto;
          }

          .guide-nav-item span {
            display: none;
          }

          .guide-nav-item svg {
            margin: 0;
          }

          .status-grid, .status-grid.compact {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default Guide;
